import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';

const fmt = (v) => (v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtCompact = (v) => {
  const abs = Math.abs(v || 0);
  if (abs >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${Math.round(v / 1000)}k`;
  return Math.round(v || 0).toString();
};

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export default function CalendarView({ movements }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [selectedDay, setSelectedDay] = useState(null);

  const movementsByDay = useMemo(() => {
    const map = {};
    for (const m of movements || []) {
      if (!m.date) continue;
      const key = m.date.slice(0, 10);
      if (!map[key]) map[key] = { in: 0, out: 0, items: [] };
      if (m.direction === 'in') map[key].in += m.amount || 0;
      else map[key].out += m.amount || 0;
      map[key].items.push(m);
    }
    return map;
  }, [movements]);

  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const last = new Date(cursor.y, cursor.m + 1, 0);
    // Monday-first index (Mon=0...Sun=6)
    const startIdx = (first.getDay() + 6) % 7;
    const totalDays = last.getDate();
    const arr = [];
    for (let i = 0; i < startIdx; i++) arr.push(null);
    for (let d = 1; d <= totalDays; d++) {
      const dt = new Date(cursor.y, cursor.m, d);
      const key = dt.toISOString().slice(0, 10);
      arr.push({ day: d, key, ...movementsByDay[key] });
    }
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor, movementsByDay]);

  const monthTotals = useMemo(() => {
    let inSum = 0, outSum = 0, cntIn = 0, cntOut = 0;
    for (const c of cells) {
      if (!c) continue;
      if (c.in) { inSum += c.in; cntIn += (c.items?.filter(i => i.direction === 'in').length || 0); }
      if (c.out) { outSum += c.out; cntOut += (c.items?.filter(i => i.direction === 'out').length || 0); }
    }
    return { inSum, outSum, net: inSum - outSum, cntIn, cntOut };
  }, [cells]);

  const move = (delta) => {
    setCursor(({ y, m }) => {
      let nm = m + delta, ny = y;
      if (nm < 0) { nm += 12; ny -= 1; }
      if (nm > 11) { nm -= 12; ny += 1; }
      return { y: ny, m: nm };
    });
    setSelectedDay(null);
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-3" data-testid="treasury-calendar-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <h3 className="text-lg font-semibold w-44 text-center">{MESI[cursor.m]} {cursor.y}</h3>
          <Button size="sm" variant="outline" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }}>Oggi</Button>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 text-emerald-700"><TrendingUp className="w-3 h-3" />{fmt(monthTotals.inSum)} <span className="text-slate-400">({monthTotals.cntIn})</span></span>
          <span className="flex items-center gap-1 text-red-700"><TrendingDown className="w-3 h-3" />{fmt(monthTotals.outSum)} <span className="text-slate-400">({monthTotals.cntOut})</span></span>
          <span className={`font-mono font-semibold ${monthTotals.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Netto: {fmt(monthTotals.net)}</span>
        </div>
      </div>

      <Card><CardContent className="p-3">
        {/* Days header */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {GIORNI.map(g => <div key={g} className="text-[10px] uppercase tracking-wider text-slate-500 text-center font-semibold py-1">{g}</div>)}
        </div>
        {/* Cells */}
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, idx) => {
            if (!c) return <div key={idx} className="h-24 bg-slate-50/40 rounded" />;
            const isToday = c.key === today;
            const isSelected = selectedDay === c.key;
            const hasIn = (c.in || 0) > 0;
            const hasOut = (c.out || 0) > 0;
            const net = (c.in || 0) - (c.out || 0);
            return (
              <button
                key={idx}
                onClick={() => setSelectedDay(isSelected ? null : c.key)}
                data-testid={`cal-day-${c.day}`}
                className={`h-24 text-left rounded border transition-all p-1.5 hover:shadow-md hover:-translate-y-0.5
                  ${isToday ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-200'}
                  ${isSelected ? 'bg-slate-900 text-white' : 'bg-white'}
                `}
              >
                <div className="flex items-start justify-between">
                  <span className={`text-xs font-bold ${isToday && !isSelected ? 'text-slate-900' : ''}`}>{c.day}</span>
                  {(hasIn || hasOut) && (
                    <span className={`text-[9px] font-mono font-semibold ${isSelected ? 'text-white' : net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {net >= 0 ? '+' : ''}{fmtCompact(net)}
                    </span>
                  )}
                </div>
                <div className="mt-1 space-y-0.5">
                  {hasIn && (
                    <div className={`text-[9px] flex items-center justify-between gap-1 px-1 py-0.5 rounded ${isSelected ? 'bg-emerald-500/30 text-emerald-100' : 'bg-emerald-50 text-emerald-700'}`}>
                      <TrendingUp className="w-2.5 h-2.5 shrink-0" />
                      <span className="font-mono truncate">{fmtCompact(c.in)}</span>
                    </div>
                  )}
                  {hasOut && (
                    <div className={`text-[9px] flex items-center justify-between gap-1 px-1 py-0.5 rounded ${isSelected ? 'bg-red-500/30 text-red-100' : 'bg-red-50 text-red-700'}`}>
                      <TrendingDown className="w-2.5 h-2.5 shrink-0" />
                      <span className="font-mono truncate">{fmtCompact(c.out)}</span>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </CardContent></Card>

      {/* Day detail */}
      {selectedDay && (
        <Card data-testid="cal-day-detail"><CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold">Dettaglio {new Date(selectedDay).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h4>
            <Button size="sm" variant="ghost" onClick={() => setSelectedDay(null)}>Chiudi</Button>
          </div>
          {!movementsByDay[selectedDay] ? (
            <p className="text-xs text-slate-400 py-4 text-center">Nessuna scadenza in questa giornata.</p>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="border-b text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="py-1.5 px-2">Tipo</th><th className="py-1.5 px-2">Anagrafica</th>
                <th className="py-1.5 px-2">Categoria</th><th className="py-1.5 px-2 text-right">Importo</th>
                <th className="py-1.5 px-2">Stato</th>
              </tr></thead>
              <tbody>
                {movementsByDay[selectedDay].items.map(m => (
                  <tr key={m.id} className="border-b">
                    <td className="py-1.5 px-2">{m.direction === 'in' ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px]">↑ Entrata</Badge> : <Badge className="bg-red-50 text-red-700 border border-red-200 text-[9px]">↓ Uscita</Badge>}</td>
                    <td className="py-1.5 px-2">{m.party_name || '—'} {m.is_anticipo && <Badge className="bg-blue-50 text-blue-700 border border-blue-200 ml-1 text-[8px]">Anticipo</Badge>}</td>
                    <td className="py-1.5 px-2">{m.category_name || '—'}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{fmt(m.amount)}</td>
                    <td className="py-1.5 px-2"><Badge className="text-[9px]">{m.stato}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent></Card>
      )}

      {/* Legenda */}
      <div className="flex items-center gap-4 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200"></span>Entrata prevista</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-200"></span>Uscita prevista</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border-2 border-slate-900"></span>Oggi</span>
        <span>Clicca un giorno per il dettaglio scadenze.</span>
      </div>
    </div>
  );
}
